import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Tooltip } from 'antd';
import styled from 'styled-components';

import MenuIcon from 'components/Header/MenuIcon';
import { COMMON_COLOR } from 'utils/style';

export type ChatMessageType = {
  text: string;
  sender: string;
  errorType?: 'api' | 'token' | null;
  cache_key?: string | null;
};

type ChatMessageInputProps = {
  submitMessage: (message: ChatMessageType) => void;
  handleReStartChat: () => void;
};

const ChatMessageInput: React.FC<ChatMessageInputProps> = ({
  submitMessage,
  handleReStartChat
}) => {
  const tokens = useSelector((state: any) => state.aiChatBot.tokens);
  const loading = useSelector((state: any) => state.aiChatBot.loading);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const consecutiveApiErrors = useSelector((state: any) => {
    return state.aiChatBot.consecutiveApiErrors;
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (tokens.unused_tokens !== 0 && e.key === 'Enter' && !e.shiftKey) {
      if (loading) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = () => {
    if (tokens.unused_tokens === 0) return;
    const trimmed = inputValue.trim();
    if (trimmed.length === 0) return;
    submitMessage({ text: trimmed, sender: 'user' });
    setInputValue('');
  };

  return (
    <ChatMessagesInputMainWrapper isRestartChat={consecutiveApiErrors >= 2}>
      {consecutiveApiErrors >= 2 ? (
        <ChatTokenErrorWrapper isRestartChat={true}>
          <span className="chat-token-error">
            <MenuIcon color={'#FAAD14'} src={'info'} />
            We’re experiencing some issues. Please start a new session.
          </span>
          <button
            style={{
              marginTop: 12,
              background: COMMON_COLOR.BLUE,
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '8px 16px',
              cursor: 'pointer',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
            onClick={() => handleReStartChat()}
          >
            <MenuIcon src={'retry'} color="#ffffff" /> New Session
          </button>
        </ChatTokenErrorWrapper>
      ) : (
        <>
          <ChatTokenErrorWrapper tokenError={tokens.unused_tokens === 0}>
            {tokens.unused_tokens === 0 && (
              <span className="chat-token-error">
                <MenuIcon color={'#FAAD14'} src={'info'} /> You’ve used up all your tokens
              </span>
            )}
            <ChatMessagesInputWrapper
              onSubmit={e => {
                e.preventDefault();
                handleSend();
              }}
            >
              <textarea
                ref={inputRef}
                placeholder="Type here..."
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                autoComplete="off"
                aria-label="Type your message"
                style={{ minHeight: 120, maxHeight: 120, overflowY: 'auto', resize: 'none' }}
              />
              <button
                type="submit"
                disabled={inputValue.trim().length === 0 || tokens.unused_tokens === 0 || loading}
                aria-label="Send message"
              >
                <MenuIcon src={'send_arrow'} />
              </button>
            </ChatMessagesInputWrapper>
          </ChatTokenErrorWrapper>
          <AiInfo>
            eMind is in Beta and can make mistakes. Be sure to double check important information.{' '}
            <Tooltip title="Restart conversation" placement="top">
              <ResetButton
                onClick={() => handleReStartChat()}
                disabled={loading}
                aria-label="Reset conversation"
              >
                <MenuIcon src={'retry'} />
              </ResetButton>
            </Tooltip>
          </AiInfo>
        </>
      )}
    </ChatMessagesInputMainWrapper>
  );
};

export default ChatMessageInput;

const ChatMessagesInputMainWrapper = styled.div<{ isRestartChat?: boolean }>`
  padding: 12px;
  width: 100%;
  background: linear-gradient(180deg, #ffffff 0%, #d6eff9 50%, #ede9fe 100%);
  ${({ isRestartChat }) =>
    isRestartChat &&
    `
      background: transparent;
    `}
`;

const ChatMessagesInputWrapper = styled.form`
  display: flex;
  justify-content: space-between;
  align-items: end;
  gap: 8px;
  padding: 8px 8px 8px 16px;
  width: 100%;
  border: 1px solid ${COMMON_COLOR.GREY_2};
  background: ${COMMON_COLOR.WHITE};
  border-radius: 8px;
  box-shadow: 0px 2px 8px 0px #00000026;

  button {
    background: ${COMMON_COLOR.BLUE};
    border: none;
    cursor: pointer;
    height: 30px;
    width: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  button:disabled {
    background: ${COMMON_COLOR.GREY_7};
    cursor: not-allowed;
    opacity: 0.6;
    pointer-events: none;
  }

  textarea {
    width: 100%;
    font-family: 'Inter', sans-serif;
    color: black;
    font-weight: 400;
    font-style: Regular;
    font-size: 14px;
    leading-trim: NONE;
    line-height: 20px;
    letter-spacing: 0%;
    border: none;
    outline: none;
    padding: 0;
    max-height: 120px;
    overflow-y: auto;
    resize: none;
  }
`;

interface ChatTokenErrorWrapperProps {
  tokenError?: boolean;
  isRestartChat?: boolean;
}

const ChatTokenErrorWrapper = styled.div<ChatTokenErrorWrapperProps>`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: space-between;
  color: white;
  transition: border 0.2s;
  ${({ tokenError }) =>
    tokenError &&
    `
      background: ${COMMON_COLOR.GREY_1};
      border-radius: 16px 16px 24px 24px;
    `}

  ${({ isRestartChat }) =>
    isRestartChat &&
    `
      justify-content: center;
      height: 180px;
    `}


  .chat-token-error {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #de9400;
    padding: 6px 0;
  }
`;

const AiInfo = styled.p`
  text-align: center;
  margin: 4px 6px 0 6px;
  font-size: 12px;
  color: ${COMMON_COLOR.GREY_3};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const ResetButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  margin-left: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  transition: opacity 0.2s;
  vertical-align: middle;

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 0.5 : 0.8)};
  }

  &:active {
    opacity: 0.6;
  }
`;
